import React, { useState, useMemo, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ChevronRight, AlertTriangle, Layers, Link2, Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { currencySymbol, formatRate, formatRateSym } from '@/lib/calendar-utils'
import { Link } from '@inertiajs/react'
import {
  computeRunningTotals,
  engineComputedTotal,
  linkedOffsetLabel,
  sameRoomAdjustment,
  sameRoomEngineLabel,
  sameRoomEngineRate,
  stageLabel,
} from '@/lib/pipeline-utils'
import { formatPickupRooms } from '@/lib/plain-language'
import EngineDetails from '@/components/EngineDetails'
import AdjustmentLabel, { adjustmentDiagnostics, clearanceTerminalNote } from './AdjustmentContext'
import { SOURCE_LABELS } from './RateHistoryChart'
import { nestRoomRows } from '@/lib/room-type-tree'
import { useFigureScope } from '@/lib/figure-scope'
import type { RoomType, RateData, OccupancyDay, Adjustment, AdrDay, RateHistoryEntry, RoomOccupancyEntry, ListingPrice } from '@/types/calendar'

// The child-link glyph the product owner specified for indented child rows —
// shared visual language with the Rooms Setup page and the calendar filters.
const LINK_GLYPH = 'M19 15l-6 6-1.42-1.42L15.17 16H4V4h2v10h9.17l-3.59-3.58L13 9l6 6z'

interface DayDetailOverviewProps {
  date: string
  roomTypes: RoomType[]
  rates: Record<string, RateData>
  occupancy?: OccupancyDay
  roomOccupancy?: Record<string, RoomOccupancyEntry>
  totalRooms: number
  currency: string
  selectedRoomTypeId?: number
  adrData?: Record<string, AdrDay>
  rateHistory?: Record<string, RateHistoryEntry[]>
  /** True when rooms display prices from DIFFERENT rate plans — rows then label
   *  each price with its own plan name (uniform case is covered by the modal
   *  header chip). Computed once in DayDetailModal from backend plan identity. */
  mixedPlans?: boolean
  /** Enables the "View target" link on Target Pressure rows (additive; link is
   *  omitted when absent, e.g. older call sites / tests). */
  propertyBasePath?: string
}

function restrictionsList(rate: RateData): string {
  const parts: string[] = []
  if (rate.stopSell) parts.push('Stop Sell')
  if (rate.closedToArrival) parts.push('CTA')
  if (rate.closedToDeparture) parts.push('CTD')
  if (rate.minStayArrival != null && rate.minStayArrival > 1) parts.push(`Min ${rate.minStayArrival}`)
  if (rate.maxStay != null) parts.push(`Max ${rate.maxStay}`)
  return parts.join(', ')
}

function demandLabel(pct: number): { text: string; dotColor: string; textColor: string } {
  if (pct >= 80) return { text: 'High Demand', dotColor: 'bg-emerald-500', textColor: 'text-emerald-600' }
  if (pct >= 60) return { text: 'Moderate', dotColor: 'bg-amber-500', textColor: 'text-amber-600' }
  return { text: 'Low Demand', dotColor: 'bg-slate-400', textColor: 'text-slate-500' }
}

function rateColorClass(rate: RateData): string {
  if (rate.source === 'manual_override') return 'text-amber-700'
  const adjs = rate.computationDetails?.adjustments
  if (!adjs) return 'text-foreground'
  const baseAdj = adjs.find((a) => a.stage === 'base_rate')
  const baseRate = baseAdj?.value ?? null
  if (baseRate != null && rate.rateAmount > baseRate) return 'text-emerald-700'
  if (baseRate != null && rate.rateAmount < baseRate) return 'text-rose-700'
  return 'text-foreground'
}

function BreakdownSectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-t border-border/30 pt-2.5 mt-1 text-[11px] font-bold uppercase tracking-[0.12em] text-primary/50">
      {children}
    </div>
  )
}

/** Pickup KPI tile — pickup is ROOMS (locked decision), never percentage points. */
function PickupStat({ rooms, period }: { rooms: number | null; period: string }) {
  const isPositive = rooms != null && rooms > 0
  const isNegative = rooms != null && rooms < 0
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2 text-center',
        isPositive
          ? 'bg-emerald-50/80 border-emerald-200'
          : isNegative
            ? 'bg-rose-50/80 border-rose-200'
            : 'bg-slate-50/60 border-border/60',
      )}
    >
      <div
        className={cn(
          'text-base font-extrabold tabular-nums leading-tight',
          isPositive ? 'text-emerald-700' : isNegative ? 'text-rose-700' : 'text-muted-foreground',
        )}
      >
        {formatPickupRooms(rooms)}
      </div>
      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {period}
      </div>
    </div>
  )
}

function PmsSyncLine({
  pushedRateAmount,
  syncStatus,
  sym,
}: {
  pushedRateAmount: number | null
  syncStatus: 'synced' | 'pending' | 'never_pushed'
  sym: string
}) {
  return (
    <div className="flex items-center justify-between text-[13px] mt-1">
      <span className="text-muted-foreground">Pushed to PMS</span>
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 rounded-full',
            syncStatus === 'synced'
              ? 'bg-emerald-500'
              : syncStatus === 'pending'
                ? 'bg-amber-500'
                : 'bg-slate-300',
          )}
        />
        <span className="tabular-nums font-medium text-muted-foreground">
          {pushedRateAmount != null ? formatRateSym(pushedRateAmount, sym) : '--'}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {syncStatus === 'synced' ? 'In sync' : syncStatus === 'pending' ? 'Pending push' : 'Never pushed'}
        </span>
      </div>
    </div>
  )
}

/**
 * COMBINED INVENTORY: how every listing sells this room that night (owner ruling 2026-10-26). The
 * calendar lists the building's rooms, so a room the building sells on several listings is one row, and
 * this is where each listing's own price shows: the lead's first, then each following listing's at its
 * own stored price with its offset, and the night's rooms sold by listing. Every figure and sentence is
 * the server's (App::ListingPrices, the split from the calendar controller); nothing is computed here.
 */
function ListingsSection({
  listings,
  split,
  sym,
}: {
  listings: ListingPrice[]
  split: string | null
  sym: string
}) {
  return (
    <div className="space-y-1.5" data-testid="listings-section">
      <BreakdownSectionHeader>Listings</BreakdownSectionHeader>
      {listings.map((line) => (
        <div key={line.propertyId} className="flex items-center justify-between gap-3 text-[13px]">
          <span className="flex min-w-0 items-center gap-1.5">
            {line.isLead ? (
              <Crown className="h-3 w-3 shrink-0 text-primary" aria-label="Lead listing" />
            ) : (
              <Link2 className="h-3 w-3 shrink-0 text-primary/70" aria-hidden="true" />
            )}
            <span className="truncate text-foreground">{line.name}</span>
            {line.offsetLabel && (
              <span className="shrink-0 rounded-full border border-primary/20 bg-primary/5 px-1.5 py-px text-[10px] font-medium tabular-nums text-primary">
                {line.offsetLabel}
              </span>
            )}
          </span>
          <span className="font-semibold tabular-nums text-foreground">{formatRateSym(line.rateAmount, sym)}</span>
        </div>
      ))}
      {split && (
        <div className="flex items-baseline justify-between gap-3 text-[13px]">
          <span className="shrink-0 text-muted-foreground">Rooms sold</span>
          <span className="text-right tabular-nums text-muted-foreground">{split}</span>
        </div>
      )}
    </div>
  )
}

function timeAgoShort(iso: string): string {
  const now = new Date()
  const then = new Date(iso)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  const remainHours = diffHours % 24
  if (diffDays < 7) return remainHours > 0 ? `${diffDays}d ${remainHours}h ago` : `${diffDays}d ago`
  return `${diffDays}d ago`
}

function RateHistorySection({
  entries,
  sym,
}: {
  entries: RateHistoryEntry[]
  sym: string
}) {
  const [expanded, setExpanded] = React.useState(false)
  if (!entries || entries.length === 0) return null

  const visible = expanded ? entries : entries.slice(0, 3)
  const remaining = entries.length - 3

  return (
    <div className="border-t border-border/30 pt-2 mt-2">
      <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground/50 mb-1">
        <span>Rate History</span>
      </div>
      <div className="space-y-0.5">
        {visible.map((entry, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-[13px]">
            <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
              <span className="shrink-0">{timeAgoShort(entry.at)}</span>
              {/* WHY the price moved, whenever it was not the ordinary two-hourly recompute.
                  This list is where the origin incident happened: a stepped-down clearance price
                  reading as a bare "computation" is the engine looking asleep while it works.
                  Amber is the CUT. `clearance_ended` — the rule letting the price go back to
                  normal — falls through to the neutral tone deliberately (operator ruling,
                  2026-09-02): it is a rise, and the amber badge on it read as a discount. */}
              {SOURCE_LABELS[entry.source] && entry.source !== 'computation' && (
                <span
                  className={cn(
                    'shrink-0 rounded px-1 text-[10px] font-semibold leading-tight',
                    entry.source === 'clearance'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-slate-100 text-slate-600',
                  )}
                >
                  {SOURCE_LABELS[entry.source]}
                </span>
              )}
            </span>
            <span className="tabular-nums font-medium text-muted-foreground">
              {formatRateSym(entry.amount, sym)}
            </span>
          </div>
        ))}
      </div>
      {!expanded && remaining > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 text-[12px] font-medium text-primary/70 hover:text-primary transition-colors"
        >
          +{remaining} more
        </button>
      )}
      {expanded && entries.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-1 text-[12px] font-medium text-primary/70 hover:text-primary transition-colors"
        >
          Show less
        </button>
      )}
    </div>
  )
}

function PipelineBreakdown({
  adjustments,
  finalRate,
  roomType,
  sym,
  pushedRateAmount,
  syncStatus,
  historyEntries,
  propertyBasePath,
  date,
  listingsSection,
}: {
  adjustments: Adjustment[]
  finalRate: number
  roomType?: RoomType
  sym: string
  pushedRateAmount: number | null
  syncStatus: 'synced' | 'pending' | 'never_pushed'
  historyEntries?: RateHistoryEntry[]
  propertyBasePath?: string
  date?: string
  /** Combined inventory: each listing's price that night, drawn after this listing's own final price. */
  listingsSection?: React.ReactNode
}) {
  const steps = computeRunningTotals(adjustments, sym)
  const baseStep = steps.find((s) => s.stage === 'base_rate')
  const linkedStep = steps.find((s) => s.stage === 'linked_parent')
  // A Same room (combined inventory) sells at its twin's published price, copied whole: one step, no
  // base rate, no adjustments and no guardrails of its own.
  const sameRoomAdj = sameRoomAdjustment(adjustments)
  const sameRoomEngine = sameRoomAdj ? sameRoomEngineRate(sameRoomAdj) : null
  // Its offset from the twin, when it carries one: the step after the copy, ending on its own price.
  const sameRoomOffsetStep = steps.find((s) => s.stage === 'same_room_offset')
  const adjustmentSteps = steps.filter(
    (s) => !['base_rate', 'linked_parent', 'clamp', 'rounding', 'override', 'restrictions', 'same_room', 'same_room_offset'].includes(s.stage),
  )
  const roundingStep = steps.find((s) => s.stage === 'rounding')
  const overrideStep = steps.find((s) => s.stage === 'override')
  const clampAdj = adjustments.find((a) => a.stage === 'clamp')
  const clampType = clampAdj?.type
  const isOverride = overrideStep != null

  const minRate = roomType?.minRate ?? null
  const maxRate = roomType?.maxRate ?? null
  const overrideAboveCeiling = isOverride && maxRate != null && finalRate > maxRate
  const overrideBelowFloor = isOverride && minRate != null && finalRate < minRate

  const computedRate = engineComputedTotal(steps)

  const baseRate = baseStep?.runningTotal ?? null
  const finalColor = isOverride
    ? 'text-amber-700'
    : baseRate != null && finalRate > baseRate
      ? 'text-emerald-700'
      : baseRate != null && finalRate < baseRate
        ? 'text-rose-700'
        : 'text-foreground'

  return (
    <div className="bg-slate-50/50 px-5 py-3 space-y-1.5">
      {/* BASE */}
      {baseStep && (
        <>
          <BreakdownSectionHeader>Base</BreakdownSectionHeader>
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-muted-foreground">{baseStep.label}</span>
            <span className="font-semibold tabular-nums text-foreground">
              {formatRateSym(baseStep.runningTotal, sym)}
            </span>
          </div>
        </>
      )}

      {/* BASE — linked child: parent anchor + linked offset */}
      {linkedStep && (
        <>
          <BreakdownSectionHeader>Base</BreakdownSectionHeader>
          <div className="flex items-center justify-between text-[13px]">
            <AdjustmentLabel adj={linkedStep.adjustment}>Parent</AdjustmentLabel>
            <span className="font-semibold tabular-nums text-foreground">
              {formatRateSym(linkedStep.adjustment.anchor_rate ?? 0, sym)}
            </span>
          </div>
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-muted-foreground">Linked offset</span>
            <span className="tabular-nums">
              <span className={cn('font-medium', linkedStep.colorClass)}>
                {linkedOffsetLabel(linkedStep.adjustment, sym)}
              </span>
              <span className="text-muted-foreground ml-2">
                {formatRateSym(linkedStep.runningTotal, sym)}
              </span>
            </span>
          </div>
        </>
      )}

      {/* BASE: Same room, the twin's published price, copied */}
      {sameRoomAdj && (
        <>
          <BreakdownSectionHeader>Base</BreakdownSectionHeader>
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-muted-foreground">{stageLabel('same_room', sameRoomAdj.description)}</span>
            <span className="font-semibold tabular-nums text-foreground">
              {formatRateSym(sameRoomAdj.copied_rate ?? finalRate, sym)}
            </span>
          </div>
          {sameRoomEngine != null && (
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-muted-foreground">{sameRoomEngineLabel(sameRoomAdj.twin_room_type_name)}</span>
              <span className="tabular-nums text-muted-foreground">{formatRateSym(sameRoomEngine, sym)}</span>
            </div>
          )}
          {sameRoomOffsetStep && (
            <div className="flex items-center justify-between text-[13px]">
              <AdjustmentLabel adj={sameRoomOffsetStep.adjustment}>{sameRoomOffsetStep.label}</AdjustmentLabel>
              <span className="tabular-nums">
                <span className={cn('font-medium', sameRoomOffsetStep.colorClass)}>{sameRoomOffsetStep.effect}</span>
                <span className="text-muted-foreground ml-2">{formatRateSym(sameRoomOffsetStep.runningTotal, sym)}</span>
              </span>
            </div>
          )}
        </>
      )}

      {/* ADJUSTMENTS */}
      {adjustmentSteps.length > 0 && (
        <>
          <BreakdownSectionHeader>Adjustments</BreakdownSectionHeader>
          {adjustmentSteps.map((step, i) => {
            const adj = step.adjustment
            const diagnostics = adjustmentDiagnostics(adj)
            const terminalNote = clearanceTerminalNote(adj)
            return (
              <React.Fragment key={`${step.stage}-${i}`}>
                <div className="group/row relative flex items-center justify-between text-[13px]">
                  {/* Left cluster: the row label (with its hover Info icon) and, for the
                      Target Pressure stage, the "View target" link sitting inline right
                      after the tooltip icon. justify-between still pushes the value right. */}
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <AdjustmentLabel adj={adj}>{step.label}</AdjustmentLabel>
                    {adj.stage === 'target_pressure' && adj.type === 'forecast_gap' && propertyBasePath != null && (() => {
                      // Integer target month (1–12) — prefer the backend's stamped target_month,
                      // fall back to the stay date's month (same rule as CalendarDayPopup).
                      const targetMonth = adj.target_month ?? (date ? parseInt(date.substring(5, 7), 10) : null)
                      if (!targetMonth) return null
                      return (
                        <Link
                          href={`${propertyBasePath}/targets/${targetMonth}`}
                          className="text-[11px] text-primary hover:underline whitespace-nowrap"
                        >
                          View target
                        </Link>
                      )
                    })()}
                  </span>
                  {adj.stage === 'rate_stability' && adj.computed_rate != null && adj.applied_rate != null ? (
                    <span className="tabular-nums font-medium">
                      <span className="text-muted-foreground">{formatRateSym(adj.computed_rate, sym)}</span>
                      <span className={cn('mx-1', adj.applied_rate > adj.computed_rate ? 'text-emerald-500' : 'text-rose-500')}>→</span>
                      <span className="text-muted-foreground">{formatRateSym(adj.applied_rate, sym)}</span>
                    </span>
                  ) : (
                    <span className="tabular-nums">
                      <span className={cn('font-medium', step.colorClass)}>{step.effect}</span>
                      <span className="text-muted-foreground ml-2">{formatRateSym(step.runningTotal, sym)}</span>
                    </span>
                  )}
                  {/* Raw engine internals for this stage — icon-only affordance in the row's
                      right gutter (inside the container's px-5 padding, so the running totals
                      stay flush-aligned with the rows below). The popover is the ONLY place
                      the bare pace ratio / σ / samples / amplifier appear. */}
                  {diagnostics.length > 0 && (
                    <span className="absolute top-1/2 -right-[18px] -translate-y-1/2">
                      <EngineDetails variant="icon">
                        {diagnostics.map((d, j) => (
                          <EngineDetails.Row key={j} label={d.label} value={d.value} />
                        ))}
                      </EngineDetails>
                    </span>
                  )}
                </div>
                {/* D13 (C6) — the date's own day-type gap ("Weekdays vs your target +5.56 pts")
                    lives in the row's AdjustmentLabel tooltip (plain conclusion,
                    AdjustmentContext). The "View target" link now sits inline in the left
                    cluster of the adjustment row above, next to the label's Info icon. */}
                {/* C8 dilution guard + C7 ADR floor — the withheld-discount and ADR-floor
                    conclusions moved OFF the surface into the row's AdjustmentLabel tooltip
                    (short plain wording, AdjustmentContext). Only "View target" stays visible. */}
                {/* Event-adjusted demand with no booking history yet — plain note
                    (the worded conclusion also lives on the row's hover label). */}
                {/* Clearance — WHAT STOPPED THE DESCENT, when a bound rather than the rung
                    decided the price. The hover card carries the same reason inside the stage's
                    own sentence; this surface has no such sentence, so it says it here. */}
                {terminalNote && (
                  <div className="pl-3 mt-0.5">
                    <p className="text-[11px] leading-relaxed text-muted-foreground">{terminalNote}</p>
                  </div>
                )}
                {adj.stage === 'property_demand' && adj.baseline_adjusted && adj.type === 'event_deferred' && (
                  <div className="pl-3 mt-0.5">
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                      No booking history yet — trusting your event prediction. This will settle as bookings come in.
                    </p>
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </>
      )}

      {/* Rounding */}
      {roundingStep && roundingStep.adjustment.original !== roundingStep.adjustment.rounded && (
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-muted-foreground">Rounding</span>
          <span className="tabular-nums text-muted-foreground">
            {formatRateSym(roundingStep.runningTotal, sym)}
          </span>
        </div>
      )}

      {/* Computed subtotal (only shown when override exists) */}
      {isOverride && computedRate != null && (
        <div className="border-t border-border/40 pt-1.5 mt-1.5">
          <div className="flex items-center justify-between text-[13px]">
            <span className="font-semibold text-foreground">Computed</span>
            <span className="font-semibold tabular-nums text-foreground">
              {formatRateSym(computedRate, sym)}
            </span>
          </div>
        </div>
      )}

      {/* GUARDRAILS (a Same room has none of its own) */}
      {(minRate != null || maxRate != null) && !sameRoomAdj && (
        <>
          <BreakdownSectionHeader>Guardrails</BreakdownSectionHeader>
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-muted-foreground">Floor</span>
            <span
              className={cn(
                'tabular-nums',
                clampType === 'floor' ? 'font-medium text-amber-600' : 'text-muted-foreground',
              )}
            >
              {minRate != null ? formatRateSym(minRate, sym) : '—'}
              {clampType === 'floor' && ' applied'}
            </span>
          </div>
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-muted-foreground">Ceiling</span>
            <span
              className={cn(
                'tabular-nums',
                clampType === 'ceiling' ? 'font-medium text-amber-600' : 'text-muted-foreground',
              )}
            >
              {maxRate != null ? formatRateSym(maxRate, sym) : '—'}
              {clampType === 'ceiling' && ' applied'}
            </span>
          </div>
        </>
      )}

      {/* OVERRIDE */}
      {isOverride && (
        <div className="rounded-lg bg-amber-50 border border-amber-400/50 px-3 py-2 mt-1">
          <div className="flex items-center justify-between text-[13px]">
            <span className="font-semibold text-amber-800">Override</span>
            <span className="font-bold tabular-nums text-amber-800">{formatRateSym(finalRate, sym)}</span>
          </div>
          {overrideStep.adjustment.description && (
            <p className="text-[11px] text-amber-700/80 mt-0.5">
              {overrideStep.adjustment.description.replace('Manual override: ', '')}
            </p>
          )}
          {(overrideAboveCeiling || overrideBelowFloor) && (
            <div className="flex items-center gap-1 mt-1">
              <AlertTriangle className="h-3 w-3 text-amber-600" />
              <span className="text-[10px] text-amber-700">
                {overrideAboveCeiling
                  ? `Above ceiling (${formatRateSym(maxRate!, sym)})`
                  : `Below floor (${formatRateSym(minRate!, sym)})`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* FINAL */}
      <div className="border-t border-border/40 pt-2 mt-2">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-bold text-foreground">Final Rate</span>
          <span className={cn('text-lg font-extrabold tabular-nums', finalColor)}>
            {formatRateSym(finalRate, sym)}
          </span>
        </div>
      </div>

      <PmsSyncLine pushedRateAmount={pushedRateAmount} syncStatus={syncStatus} sym={sym} />
      {listingsSection}
      {historyEntries && historyEntries.length > 0 && (
        <RateHistorySection entries={historyEntries} sym={sym} />
      )}
    </div>
  )
}

/** @deprecated DerivedBreakdown removed — all rooms now use PipelineBreakdown */

export default function DayDetailOverview({
  date,
  roomTypes,
  rates,
  occupancy,
  roomOccupancy,
  totalRooms,
  currency,
  selectedRoomTypeId,
  adrData,
  rateHistory,
  mixedPlans,
  propertyBasePath,
}: DayDetailOverviewProps) {
  // THE LADDER FOLLOWS THE ACTUAL PARENT, WHEREVER IT LIVES — the same flat list Rooms Setup draws.
  // The backend orders this hotel-major, so indenting on a bare `parentRoomTypeId` drew a
  // cross-hotel child under whichever unrelated room happened to precede it. Nesting on identity
  // puts it under the room it really follows, and the indent plus the curved arrow say so on their
  // own — no sentence required.
  const ladder = useMemo(
    () => nestRoomRows(roomTypes, (rt) => rt.parentRoomTypeId ?? null, { crossHotelLinks: 'nest' }),
    [roomTypes],
  )

  const defaultRtId = useMemo(
    () => selectedRoomTypeId ?? roomTypes[0]?.id ?? null,
    [roomTypes, selectedRoomTypeId],
  )
  const [expandedRtId, setExpandedRtId] = useState<number | null>(defaultRtId)

  useEffect(() => {
    setExpandedRtId(defaultRtId)
  }, [date, defaultRtId])

  const sym = currencySymbol(currency)
  // At a combined inventory the day's occupancy and ADR are the building's, and say so (server words).
  const { pooledLabel, adrLabel } = useFigureScope()

  return (
    <div className="space-y-5">
      {/* Section 1: Property Summary Stats — compact 5-card row */}
      {occupancy && (
        <TooltipProvider delayDuration={200}>
        <div className="animate-slide-in grid grid-cols-5 gap-2 px-6" style={{ animationDelay: '0ms' }}>
          {/* Occupancy + Rooms merged */}
          {(() => {
            // Recompute pct from booked/totalRooms so the percentage matches the displayed X/Y fraction.
            const pct = totalRooms > 0 ? Math.round((occupancy.booked / totalRooms) * 100) : 0
            const tier = pct >= 80 ? 'high' : pct >= 60 ? 'mid' : 'low'
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                <div
                  className={cn(
                    'rounded-lg border px-3 py-2 text-center cursor-default',
                    tier === 'high'
                      ? 'bg-emerald-100 border-emerald-300/60'
                      : tier === 'mid'
                        ? 'bg-amber-100 border-amber-300/60'
                        : 'bg-slate-100 border-slate-300/60',
                  )}
                >
                  <div
                    className={cn(
                      'text-base font-extrabold tabular-nums leading-tight',
                      tier === 'high'
                        ? 'text-emerald-800'
                        : tier === 'mid'
                          ? 'text-amber-800'
                          : 'text-slate-700',
                    )}
                  >
                    {pct}<span className="text-[11px] font-bold">%</span>
                    <span className="text-xs font-semibold text-muted-foreground ml-1">
                      {occupancy.booked}/{totalRooms}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Occupancy
                  </div>
                </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <p className="font-medium text-xs">{pooledLabel ?? 'Property'} Occupancy</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {occupancy.booked} of {totalRooms} rooms booked across all room types.
                    This drives the property-level demand signal.
                  </p>
                </TooltipContent>
              </Tooltip>
            )
          })()}

          {/* ADR */}
          {(() => {
            const adrDay = adrData?.[date]
            const adr = adrDay?.adr
            const lyAdr = adrDay?.lastYearAdr
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                <div className="rounded-lg border border-border/60 px-3 py-2 text-center cursor-default">
                  <div className="text-base font-extrabold tabular-nums leading-tight text-foreground">
                    {adr != null ? formatRateSym(adr, sym) : '—'}
                  </div>
                  <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {adrLabel ?? 'ADR'}
                    <span className="ml-1 normal-case tracking-normal font-normal">
                      LY: {lyAdr != null ? formatRateSym(lyAdr, sym) : 'N/A'}
                    </span>
                  </div>
                </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <p className="font-medium text-xs">Average Daily Rate</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Average rate across all booked rooms for this date.
                    {lyAdr != null && adr != null ? ` Last year: ${formatRateSym(lyAdr, sym)} (${((adr - lyAdr) / lyAdr * 100).toFixed(0)}% YoY).` : ''}
                  </p>
                </TooltipContent>
              </Tooltip>
            )
          })()}

          {/* Pickups — ROOMS gained/lost for this date (locked decision: pickup = rooms) */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div><PickupStat rooms={occupancy.pickup7dRooms} period="Pickup · 7 days" /></div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[220px]">
              <p className="font-medium text-xs">7-Day Pickup</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Rooms gained or lost for this date over the last 7 days. Positive values mean bookings are accelerating.
              </p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <div><PickupStat rooms={occupancy.pickup30dRooms} period="Pickup · 30 days" /></div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[220px]">
              <p className="font-medium text-xs">30-Day Pickup</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Rooms gained or lost for this date over the last 30 days. Shows the medium-term booking momentum.
              </p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <div><PickupStat rooms={occupancy.pickup60dRooms} period="Pickup · 60 days" /></div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[220px]">
              <p className="font-medium text-xs">60-Day Pickup</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Rooms gained or lost for this date over the last 60 days. Captures the long-term demand trend for this date.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        </TooltipProvider>
      )}

      {/* Section 2: All Room Types Table */}
      <div
        className="animate-slide-in border-y border-border/60 bg-white overflow-hidden"
        style={{ animationDelay: occupancy ? '50ms' : '0ms' }}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">Room Type</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>PMS</TableHead>
              <TableHead className="text-right">Restrictions</TableHead>
              <TableHead className="w-8 pr-4"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ladder.map(({ row: rt, depth }) => {
              const rate = rates[`${rt.id}:${date}`]
              const restrictions = rate ? restrictionsList(rate) : ''
              const isExpanded = expandedRtId === rt.id
              const hasPipeline =
                rate?.computationDetails?.adjustments != null && rate.computationDetails.adjustments.length > 0
              // Combined inventory: a room the building sells on several listings (a twin) says how each
              // listing sells it that night. A Same room's own row (a following listing's Occupancy
              // calendar only) carries no section: its twin's row does.
              const listings = rate?.listings ?? []
              const split = rt.sameAs ? null : roomOccupancy?.[`${rt.id}:${date}`]?.split ?? null
              const hasListings = !rt.sameAs && (listings.length > 0 || split != null)
              const listingsSection = hasListings ? <ListingsSection listings={listings} split={split} sym={sym} /> : null
              const hasDetails = hasPipeline || hasListings
              const isOverride = rate?.source === 'manual_override'
              const isChild = depth > 0
              const isParent = rt.childCount > 0

              return (
                <React.Fragment key={rt.id}>
                  <TableRow
                    className={cn(
                      'cursor-pointer select-none transition-all duration-200',
                      isExpanded
                        ? 'border-t-2 border-b-0 border-t-primary/30 bg-primary/[0.06] hover:bg-primary/[0.06]'
                        : 'opacity-80 hover:opacity-100',
                    )}
                    onClick={() => {
                      if (hasDetails) setExpandedRtId(isExpanded ? null : rt.id)
                    }}
                  >
                    <TableCell className="py-3 pl-6">
                      <div
                        className="flex items-start gap-2"
                        style={isChild ? { paddingLeft: 24 } : undefined}
                      >
                        {isChild && (
                          <svg
                            viewBox="0 0 24 24"
                            className="mt-0.5 h-4 w-4 shrink-0 text-primary/70"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d={LINK_GLYPH} />
                          </svg>
                        )}
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span
                              title={rt.name}
                              className={cn(
                                'truncate text-foreground',
                                isParent ? 'font-semibold' : 'font-medium',
                              )}
                            >
                              {rt.name}
                            </span>
                            {isParent && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-primary">
                                <Layers className="h-2.5 w-2.5" />
                                {rt.childCount}
                              </span>
                            )}
                            {isOverride && (
                              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] font-semibold border-0">
                                Override
                              </Badge>
                            )}
                          </div>
                          {/* SECOND LINE: how the room is selling, then which hotel it belongs to.
                              The badge lives here rather than beside the name because the name line
                              is the one starved of width — and a hotel is context for the row, not
                              part of its title. Wrapped so the badge still shows on a room with no
                              occupancy data, where the block below renders nothing. */}
                          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          {(() => {
                            const roomOcc = roomOccupancy?.[`${rt.id}:${date}`]
                            const pct = roomOcc?.occupancyPct ?? 0
                            const booked = roomOcc?.booked ?? 0
                            const total = roomOcc?.total ?? rt.totalUnits ?? 0
                            if (total === 0) return null
                            const demand = demandLabel(pct)
                            return (
                              // A folded row (combined inventory) counts the room across every listing, and says so.
                              <div className="flex items-center gap-1.5 text-[11px]">
                                {roomOcc?.scopeLabel && (
                                  <span className="rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary">
                                    {roomOcc.scopeLabel}
                                  </span>
                                )}
                                <span
                                  className={cn(
                                    'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
                                    demand.dotColor,
                                  )}
                                />
                                <span className="text-muted-foreground">{demand.text}</span>
                                <span className="text-muted-foreground/40">&middot;</span>
                                <span className="tabular-nums text-muted-foreground">
                                  {booked}/{total} rooms
                                </span>
                                <span className="text-muted-foreground/40">&middot;</span>
                                <span className={cn('tabular-nums font-semibold', demand.textColor)}>
                                  {pct}%
                                </span>
                              </div>
                            )
                          })()}
                          {/* A Same room is a row only on a following listing's Occupancy calendar; there its
                              tag says which room it is the same room as. */}
                          {rt.sameAs && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                              <Link2 className="h-2.5 w-2.5" aria-hidden="true" />
                              {rt.sameAs.tag ?? rt.sameAs.label}
                            </span>
                          )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      {rate ? (
                        <div className="flex flex-col">
                          <span className={cn('font-semibold tabular-nums', rateColorClass(rate))}>
                            {formatRate(rate.rateAmount, currency)}
                          </span>
                          {/* Plan label per row only when rooms show DIFFERENT plans —
                              the uniform case is named once in the modal header. */}
                          {mixedPlans && rate.ratePlanName && (
                            <span className="mt-0.5 max-w-[140px] truncate text-[10px] leading-tight text-muted-foreground">
                              {rate.ratePlanName}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-3">
                      {rate ? (
                        <div className="flex items-center gap-1.5">
                          <span
                            className={cn(
                              'inline-block h-1.5 w-1.5 rounded-full',
                              rate.syncStatus === 'synced'
                                ? 'bg-emerald-500'
                                : rate.syncStatus === 'pending'
                                  ? 'bg-amber-500'
                                  : 'bg-slate-300',
                            )}
                          />
                          <span className="tabular-nums text-sm text-muted-foreground">
                            {rate.pushedRateAmount != null
                              ? formatRate(rate.pushedRateAmount, currency)
                              : '--'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right py-3 text-sm text-muted-foreground">
                      {restrictions || '—'}
                    </TableCell>
                    <TableCell className="pr-4 py-3">
                      {hasDetails && (
                        <ChevronRight
                          className={cn(
                            'h-4 w-4 text-muted-foreground transition-transform duration-200',
                            isExpanded && 'rotate-90',
                          )}
                        />
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expandable pipeline breakdown, and how each listing sells the room */}
                  {hasDetails && (
                    <TableRow
                      className={cn(
                        'hover:bg-transparent',
                        isExpanded
                          ? 'bg-primary/[0.06] border-b-2 border-b-primary/30 hover:bg-primary/[0.06]'
                          : 'border-b border-border/30',
                      )}
                    >
                      <TableCell colSpan={5} className="p-0">
                        <div
                          className={cn(
                            'grid transition-[grid-template-rows] duration-250 ease-out',
                            isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                          )}
                        >
                          <div className="overflow-hidden">
                            {hasPipeline && rate ? (
                              <PipelineBreakdown
                                adjustments={rate.computationDetails.adjustments!}
                                finalRate={rate.rateAmount}
                                roomType={rt}
                                sym={sym}
                                pushedRateAmount={rate.pushedRateAmount}
                                syncStatus={rate.syncStatus}
                                historyEntries={rateHistory?.[`${rt.id}:${date}`]}
                                propertyBasePath={propertyBasePath}
                                date={date}
                                listingsSection={listingsSection}
                              />
                            ) : (
                              <div className="bg-slate-50/50 px-5 py-3">{listingsSection}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              )
            })}

            {roomTypes.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-sm text-muted-foreground">
                  No room types configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
